import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from "@angular/core";
import { IButtonDirective, ITooltipDirective } from "@front/m19-ui";

@Component({
  selector: "app-editable-text",
  imports: [CommonModule, ITooltipDirective, IButtonDirective],
  templateUrl: "./editable-text.component.html",
  host: {
    class: "min-w-0",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditableTextComponent {
  label = input<string>("");
  tooltipText = input<string>("");
  disable = input<boolean>(false);

  textChange = output<string>();

  writableLabel = linkedSignal(() => this.label());
  editMode = signal(false);

  private hasLabelChanged = linkedSignal(() => this.writableLabel() !== this.label());

  private readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>("inputEl");

  emitValueChange(value: string) {
    if (value && this.hasLabelChanged()) {
      this.textChange.emit(value);
      this.hasLabelChanged.set(false);
    } else if (!value) {
      this.resetWritableLabel();
    }
  }

  updateLabel(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.writableLabel.set(value);
  }

  onBlur() {
    this.emitValueAndToggleEditMode();
  }

  toggleEditMode() {
    if (this.disable()) return;
    this.editMode.set(!this.editMode());
    if (this.editMode()) {
      setTimeout(() => {
        this.focusAndSelectText();
      });
    } else {
      this.blurInput();
    }
  }

  onKeydownEnter(event: KeyboardEvent) {
    if (event.key === "Enter") {
      this.blurInput();
    }
    if (event.key === "Escape") {
      this.resetWritableLabel();
      this.blurInput();
    }
  }

  private resetWritableLabel() {
    this.writableLabel.set(this.label());
  }

  private blurInput() {
    this.inputElement().nativeElement.blur();
  }

  private focusAndSelectText() {
    this.inputElement().nativeElement.setSelectionRange(0, 0);
    this.inputElement().nativeElement.focus();
    this.inputElement().nativeElement.select();
  }

  private emitValueAndToggleEditMode() {
    this.emitValueChange(this.writableLabel());
    this.toggleEditMode();
  }
}
